import get from 'lodash/get'
import omit from 'lodash/omit'
import set from 'lodash/set'
import uniqueId from 'lodash/uniqueId'
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { GlobalStore, UseGlobalReturn } from './types'
import { Path, PathValue, UseGlobalDispatcher } from './types.internal'

interface GlobalContextType {
  store: GlobalStore
  register: (id: string, callback: () => void) => void
  unregister: (id: string) => void
  ping: () => void
}

const GlobalContext = React.createContext<GlobalContextType | null>(null)

function getValue<T, P extends Path<T>>(obj: T, path: P): PathValue<T, P> {
  return get(obj, path) as unknown as PathValue<T, P>
}
function setValue<T>(obj: GlobalStore, path: string, value: T) {
  set(obj, path, value)
}

export function useGlobal<T extends Path<GlobalStore>>(
  path: T
): UseGlobalReturn<T> {
  const ctx = useContext(GlobalContext)
  if (ctx === null)
    throw new Error('useGlobal can only be used within a GlobalProvider.')

  const myId = useRef(uniqueId('globalStoreListener'))

  const stored = useMemo(() => getValue(ctx.store, path), [ctx.store, path])
  const [current, setCurrent] = useState(stored)

  useEffect(() => {
    const id = myId.current
    const { register, unregister } = ctx

    register(id, () => {
      const value = getValue(ctx.store, path)
      setCurrent(value)
    })

    return () => unregister(id)
  }, [ctx, path])

  const setter = useCallback(
    (value: typeof stored | UseGlobalDispatcher<typeof stored>) => {
      const newValue = typeof value === 'function' ? value(current) : value
      setValue(ctx.store, path, newValue)
      ctx.ping()
    },
    [ctx, path, current]
  )

  return [current, setter]
}

interface UseGlobalProviderProps extends PropsWithChildren<{}> {
  store: GlobalStore
}

export const UseGlobalProvider: React.FC<UseGlobalProviderProps> = ({
  children,
  store,
}) => {
  const listeners = useRef<{
    [id: string]: () => void
  }>({})

  const register = useCallback((id: string, callback: () => void) => {
    listeners.current = {
      ...listeners.current,
      [id]: callback,
    }
  }, [])

  const unregister = useCallback((id: string) => {
    listeners.current = omit(listeners.current, id)
  }, [])

  const ping = useCallback(() => {
    Object.values(listeners.current).forEach(callback => callback())
  }, [])

  return (
    <GlobalContext.Provider
      value={{
        store,
        register,
        unregister,
        ping,
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}
